import React, { useState, useCallback, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
// import DevicesIcon from "@mui/icons-material/Devices";
import RightDrawer from "../../RightDrawer";
import logStore from "../../../stores/LogStore";
import { useParams } from "react-router-dom";
import deviceStore from "../../../stores/DeviceStore";
import LogFilterPanel from "../../dashboard-components/LogFilterPanel";
import {
  DEVICE_ID_TO_NAME_MAP as deviceIdToNameMap,
  FILTER_FIELD_DEFS,
} from "../../../utils/constants";
import {
  applyFieldFilters,
  isDateInRange,
} from "../../../utils/LogFilterUtils";

const renderBoldHeader = (params) => (
  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
    {params?.colDef?.headerName || ""}
  </Typography>
);

const getAutoWidth = (headerName, field, padding = 20) => {
  const baseWidth = headerName.length * 10 + padding;
  if (field === "user_local_t") return baseWidth + 60;
  return baseWidth;
};

const rawColumns = [
  { field: "id", headerName: "Log ID" },
  { field: "device_id", headerName: "Device ID" },
  { field: "device_name", headerName: "Device Name" },
  { field: "user_local_t", headerName: "Timestamp" },
  { field: "system_voltage", headerName: "System Voltage" },
  { field: "v1_set_pressure", headerName: "V1 Set Pressure" },
  { field: "v1_hyd_pressure", headerName: "V1 Hydraulic Pressure" },
  { field: "v1_ind_pressure", headerName: "V1 Indication Pressure" },
  { field: "v1_avg_line_pressure", headerName: "V1 Avg Line Pressure" },
  { field: "v1_valve_state", headerName: "V1 Valve State" },
  { field: "v2_set_pressure", headerName: "V2 Set Pressure" },
  { field: "v2_hyd_pressure", headerName: "V2 Hydraulic Pressure" },
  { field: "v2_ind_pressure", headerName: "V2 Indication Pressure" },
  { field: "v2_avg_line_pressure", headerName: "V2 Avg Line Pressure" },
];

const columns = rawColumns.map((col) => ({
  ...col,
  width: getAutoWidth(col.headerName, col.field),
  renderHeader: renderBoldHeader,
  ...(col.field === "user_local_t" && {
    renderCell: (params) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
          {params.value || "N/A"}
        </Typography>
      </Box>
    ),
  }),
}));

const DeviceLog = observer(() => {
  const { pimios_id } = useParams();
  const deviceId = pimios_id?.split("-")[0];
  const deviceName = deviceStore.getDeviceName(deviceId);

  const [selectedRow, setSelectedRow] = useState(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [filters, setFilters] = useState(
    Object.fromEntries(
      FILTER_FIELD_DEFS.filter((f) => f.key !== "device_name").map((f) => [
        f.key,
        f.type === "interval" ? { min: "", max: "" } : "",
      ])
    )
  );

  const initialFilterState = Object.fromEntries(
    FILTER_FIELD_DEFS.filter((f) => f.key !== "device_name").map((f) => [
      f.key,
      f.type === "interval" ? { min: "", max: "" } : "",
    ])
  );

  const [fromDateTime, setFromDateTime] = useState(null);
  const [toDateTime, setToDateTime] = useState(null);

  useEffect(() => {
    if (deviceId) logStore.setDevice(deviceId);
    return () => logStore.clearDevice();
  }, [deviceId]);

  const allRows = useMemo(
    () =>
      logStore.currentLogs.map((log) => {
        return {
          id: String(log.id),
          device_name: deviceName,
          ...log,
          ...log.data,
        };
      }),
    [logStore.currentLogs, deviceName]
  );

  const filteredRows = allRows.filter(
    (row) =>
      applyFieldFilters(
        row,
        filters,
        FILTER_FIELD_DEFS.filter((f) => f.key !== "device_name")
      ) && isDateInRange(row.user_local_t, fromDateTime, toDateTime)
  );

  const totalRows = filteredRows.length;
  const totalPages = Math.max(Math.ceil(totalRows / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const currentRows = filteredRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const buildPageNumbers = useCallback((total, current) => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    const range = [1];
    if (current > 3) range.push("...");
    for (
      let i = Math.max(2, current - 1);
      i <= Math.min(total - 1, current + 1);
      i++
    ) {
      range.push(i);
    }
    if (current < total - 2) range.push("...");
    range.push(total);
    return range;
  }, []);

  const pageNumbers = useMemo(
    () => buildPageNumbers(totalPages, currentPage),
    [totalPages, currentPage]
  );

  const handleRowClick = (params) => {
    setSelectedRow(params.row);
    setDrawerOpen(true);
  };

  return (
    <>
      {/* Same layout as Logs.js */}
      <Box
        className="flex justify-between"
        sx={{ mb: 2, alignItems: "flex-end", display: "flex" }}
      >
        <Box>
          <Typography variant="body1">
            Status: {logStore.connectionStatus}
          </Typography>
          <Button
            variant="contained"
            disabled={logStore.connectionStatus === "connecting"}
            sx={{ mt: 1 }}
          >
            Refresh
          </Button>
        </Box>

        <Button
          variant="outlined"
          onClick={() => setFilterDrawerOpen(true)}
          sx={{
            height: 36,
            px: 2,
            textTransform: "none",
            borderRadius: 2,
            borderColor: "#1976d2",
            backgroundColor: "transparent",
            color: "#1976d2",
            alignSelf: "flex-end",
            "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" },
          }}
        >
          More Filters
        </Button>
      </Box>

      <Box
        sx={{
          height: 500,
          width: "100%",
          overflow: "hidden",
          border: "2px solid rgba(0, 0, 0, 0.3)",
          borderRadius: "4px",
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: "#ccffcc !important",
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: "#e0f7fa !important",
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "rgba(173, 216, 230, 0.5) !important",
            border: "2px solid #ff5722 !important",
          },
          "& .MuiDataGrid-cell": { border: "none" },
          "& .MuiDataGrid-columnHeaders": { border: "none" },
          "& .MuiDataGrid-cell:focus": { outline: "none !important" },
        }}
      >
        <DataGridPro
          rows={currentRows}
          columns={columns}
          disableColumnMenu
          disableSelectionOnClick
          pagination={false}
          hideFooter={true}
          onRowClick={handleRowClick}
          loading={logStore.connectionStatus.toLowerCase() === "connecting"}
          components={{
            LoadingOverlay: () => (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255, 255, 255, 0.7)",
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
        }}
      >
        <Button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Prev
        </Button>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {pageNumbers.map((p, i) => (
            <Button
              key={i}
              onClick={() => setPage(p)}
              variant={p === currentPage ? "contained" : "text"}
              disabled={p === "..."}
            >
              {p}
            </Button>
          ))}
        </Box>
        <Button
          onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography>Rows per page:</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <RightDrawer
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={`Log Detail: ${deviceIdToNameMap[selectedRow?.device_id] || selectedRow?.device_id}`}
      >
        {selectedRow && (
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {Object.entries(selectedRow).map(([k, v]) => (
              <Typography key={k} variant="body1">
                <strong>{k}</strong>: {String(v)}
              </Typography>
            ))}
            <Box sx={{ mt: "auto", textAlign: "right", pt: 2 }}>
              <Button variant="outlined" onClick={() => setDrawerOpen(false)}>
                Close
              </Button>
            </Box>
          </Box>
        )}
      </RightDrawer>

      <LogFilterPanel
        open={isFilterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onFilterChange={(key, value) =>
          setFilters((prev) => ({ ...prev, [key]: value }))
        }
        fromDate={fromDateTime}
        toDate={toDateTime}
        setFromDate={setFromDateTime}
        setToDate={setToDateTime}
        fieldDefs={FILTER_FIELD_DEFS.filter((f) => f.key !== "device_name")}
        onClear={() => setFilters(initialFilterState)}
        onApply={() => setFilterDrawerOpen(false)}
      />
    </>
  );
});

export default DeviceLog;
